/**
 * `AgencyAccessApproval` — the landing page the client owner lands on
 * from the link-request email (ADR-0237 §6).
 *
 * The server builds the URL `${PUBLIC_DASHBOARD_URL}/settings/agency-
 * access?token=<raw>`. The owner is prompted to Approve / Decline.
 *
 *   - On mount, immediately strip the `?token=` from the URL via
 *     `history.replaceState` so the raw token never lingers in the
 *     browser's address bar / bookmarks / referrer leak.
 *   - The route is NOT anonymous: approving requires an authenticated
 *     session as the owner of the target account. If the visitor is
 *     unauthenticated, redirect to normal sign-in. **Never** create a
 *     session in the background (per
 *     docs/runbooks/magic-link-and-signup-failures.md).
 *   - Once signed in, POST `/v1/account/agency-access/preview` to
 *     resolve the requesting agency's display name + role + expiry.
 *     The preview call does NOT consume the token; every failure is
 *     uniform 401 so no enumeration oracle leaks.
 *   - Approve → POST /v1/account/agency-access/accept {approval_token}
 *     with the raw (one-shot). 401 → "This link is invalid or has
 *     expired." Any other error → surface the server message.
 *   - Decline → POST /v1/account/agency-access/decline {approval_token}
 *     (one-shot; sets `rejected_at`). 401 → invalid or expired.
 */

import { useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAuth } from '../../app/auth-context.js';
import { useAgency } from '../../app/agency-context.js';
import {
  acceptAgencyAccess,
  declineAgencyAccess,
  previewAgencyAccess,
} from '../../api/agency.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { Card, NumLabel } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface AgencyAccessApprovalProps {
  setView: (v: View) => void;
}

interface PreviewFields {
  readonly agency_name: string;
  readonly requested_role: 'admin' | 'reviewer' | 'viewer';
  readonly expires_at: string;
}

type UiState =
  | { kind: 'bootstrap' }
  | { kind: 'need_signin' }
  | { kind: 'previewing'; token: string }
  | { kind: 'ready'; token: string; preview: PreviewFields }
  | { kind: 'submitting'; token: string; preview: PreviewFields; action: 'approve' | 'decline' }
  | { kind: 'approved' }
  | { kind: 'declined' }
  | { kind: 'invalid_or_expired' }
  | { kind: 'service_error'; message: string }
  | { kind: 'error'; message: string };

export function AgencyAccessApproval({ setView }: AgencyAccessApprovalProps): JSX.Element {
  const C = useC();
  const auth = useAuth();
  const http = useOptionalHttp();
  const agency = useAgency();
  const [state, setState] = useState<UiState>({ kind: 'bootstrap' });

  // 1. Capture + scrub the token on mount. QA fix 2026-10-08:
  //    `useState<string|null>(() => { ... history.replaceState; return t })`
  //    is NOT idempotent — React StrictMode (dev) runs the initializer
  //    twice; the first call strips `?token=` from the URL, the second
  //    call then reads the (now empty) URL and returns null, so
  //    `storedToken` ends up null and the user sees "Link invalid or
  //    expired" on every StrictMode or re-mount cycle.
  //
  //    The fix splits the read from the strip: capture the token with a
  //    pure, side-effect-free initializer (idempotent across double
  //    invoke), then strip the URL once in a useEffect that itself
  //    tolerates re-run (the second strip is a no-op because the token
  //    was already removed by the first).
  const [storedToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return new URL(window.location.href).searchParams.get('token');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (storedToken === null) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('token')) {
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {
      /* best effort — the token in-memory state is what the handler consumes */
    }
    // Only ever run once per mount; the strip is idempotent across
    // StrictMode's double-mount in dev (the second pass sees the
    // already-stripped URL and short-circuits above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Resolve the auth state. Pre-cookie visitors land here from an
  //    email; redirect to sign-in. The AuthProvider has four terminal
  //    states — `authenticated`, `unauthenticated`, `service_error`,
  //    and the transitional `bootstrap`. The founder bug 2026-10-10
  //    was that this effect only handled three of them: when the auth
  //    bootstrap timed out (`service_error`), the view was stuck in
  //    `bootstrap` forever and showed "Loading…" indefinitely. We
  //    now map the service_error to an explicit UI state with a
  //    Retry affordance.
  useEffect(() => {
    if (storedToken === null || storedToken.length === 0) {
      setState({ kind: 'invalid_or_expired' });
      return;
    }
    if (auth.state.kind === 'bootstrap') return; // wait
    if (auth.state.kind === 'unauthenticated') {
      setState({ kind: 'need_signin' });
      return;
    }
    if (auth.state.kind === 'service_error') {
      // The AuthProvider could not reach the API after three
      // attempts. The approval cannot proceed without proving the
      // signed-in session — surface the state so the user knows to
      // retry rather than staring at a "Loading…" spinner forever.
      // The one in their email is still valid.
      setState({
        kind: 'service_error',
        message:
          'Sill could not be reached from this browser. Your session may still be valid — retry below.',
      });
      return;
    }
    if (auth.state.kind === 'authenticated') {
      // Only transition into 'previewing' once — the preview effect
      // below drives the resolve.
      setState((prev) =>
        prev.kind === 'bootstrap' || prev.kind === 'need_signin' || prev.kind === 'service_error'
          ? { kind: 'previewing', token: storedToken }
          : prev,
      );
    }
  }, [auth.state, storedToken]);

  // 3. Fetch the preview once the user is authenticated. The server
  //    does NOT consume the token on this call; a 401 is uniform
  //    across every failure mode (unknown token, expired, consumed,
  //    cancelled, wrong account).
  useEffect(() => {
    if (state.kind !== 'previewing') return;
    if (http === null) return;
    const token = state.token;
    let cancelled = false;
    void (async () => {
      const r = await previewAgencyAccess(http, token);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setState({
          kind: 'ready',
          token,
          preview: {
            agency_name: r.preview.agency_name,
            requested_role: r.preview.requested_role,
            expires_at: r.preview.expires_at,
          },
        });
      } else if (r.kind === 'invalid_or_expired') {
        setState({ kind: 'invalid_or_expired' });
      } else {
        setState({ kind: 'error', message: r.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, http]);

  const onApprove = async (): Promise<void> => {
    if (http === null) return;
    if (state.kind !== 'ready') return;
    const token = state.token;
    const preview = state.preview;
    setState({ kind: 'submitting', token, preview, action: 'approve' });
    const r = await acceptAgencyAccess(http, token);
    if (r.kind === 'ok') {
      setState({ kind: 'approved' });
      // Refresh features — the client may now be visible to the agency.
      void agency.refreshFeatures();
      return;
    }
    if (r.kind === 'invalid_or_expired') {
      setState({ kind: 'invalid_or_expired' });
      return;
    }
    setState({ kind: 'error', message: r.message });
  };

  const onDecline = async (): Promise<void> => {
    if (http === null) return;
    if (state.kind !== 'ready') return;
    const token = state.token;
    const preview = state.preview;
    setState({ kind: 'submitting', token, preview, action: 'decline' });
    const r = await declineAgencyAccess(http, token);
    if (r.kind === 'ok') {
      setState({ kind: 'declined' });
      return;
    }
    if (r.kind === 'invalid_or_expired') {
      setState({ kind: 'invalid_or_expired' });
      return;
    }
    setState({ kind: 'error', message: r.message });
  };

  const goSignIn = (): void => {
    // Normal sign-in. Preserve the pathname so the user comes back
    // here after verifying — the token was already scrubbed, so the
    // user will land on the "invalid or expired" branch and be told
    // to click the email link again from the new session. That's the
    // correct behavior: we never persist a raw token.
    setView('login');
  };

  const title =
    state.kind === 'approved'
      ? 'Access approved.'
      : state.kind === 'declined'
        ? 'Request declined.'
        : state.kind === 'invalid_or_expired'
          ? 'Link invalid or expired.'
          : state.kind === 'need_signin'
            ? 'Sign in to continue.'
            : state.kind === 'service_error'
              ? 'Could not reach Sill.'
              : 'Approve agency access?';

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
      <div className="mb-6">
        <NumLabel n={1}>Agency access</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(28px, 5vw, 40px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          {title}
        </h1>
      </div>

      <Card className="p-5 sm:p-6">
        {state.kind === 'bootstrap' || state.kind === 'previewing' ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 1,
            }}
          >
            Loading…
          </div>
        ) : null}

        {state.kind === 'invalid_or_expired' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              This link is invalid, has expired, or was sent to a different account.
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Open it in the browser where you are signed in as the owner of the account the request was sent to. If it still fails, ask the agency to resend the request.
            </div>
          </>
        ) : null}

        {state.kind === 'service_error' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              {state.message}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              The approval token in your email is still valid. Reload the page, or open the email again once your connection is back.
            </div>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => void auth.refresh()}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          </>
        ) : null}

        {state.kind === 'error' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.coralFg, lineHeight: 1.5 }}>
              {state.message}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Try again from the link in your email. If the problem persists, contact Sill.
            </div>
          </>
        ) : null}

        {state.kind === 'need_signin' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              Sign in as the account owner, then open this link again from your email.
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              For security, approvals happen on your own signed-in session. The approval token in this URL was removed from your address bar; the one in your email is still valid.
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={goSignIn}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Go to sign in
              </button>
            </div>
          </>
        ) : null}

        {(state.kind === 'ready' || state.kind === 'submitting') ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              <strong style={{ fontWeight: 600 }}>{state.preview.agency_name}</strong>
              {' '}is requesting <strong style={{ fontWeight: 600 }}>{state.preview.requested_role}</strong> access to your account.
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                marginTop: 6,
                letterSpacing: 1,
              }}
            >
              Expires {formatExpiry(state.preview.expires_at)}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Approving lets the agency’s members act inside your account at the requested role. You can revoke their access at any time from Account settings.
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
              <button
                type="button"
                onClick={() => void onDecline()}
                disabled={state.kind === 'submitting'}
                style={{
                  background: 'transparent',
                  color: C.muted,
                  fontFamily: sansFont,
                  fontSize: 13,
                  padding: '10px 14px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  cursor: state.kind === 'submitting' ? 'default' : 'pointer',
                  opacity: state.kind === 'submitting' ? 0.6 : 1,
                }}
              >
                {state.kind === 'submitting' && state.action === 'decline'
                  ? 'Declining…'
                  : 'Decline'}
              </button>
              <button
                type="button"
                onClick={() => void onApprove()}
                disabled={state.kind === 'submitting'}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '10px 14px',
                  border: 'none',
                  borderRadius: 2,
                  cursor: state.kind === 'submitting' ? 'default' : 'pointer',
                  opacity: state.kind === 'submitting' ? 0.7 : 1,
                  flex: 1,
                }}
              >
                {state.kind === 'submitting' && state.action === 'approve'
                  ? 'Approving…'
                  : 'Approve access'}
              </button>
            </div>
          </>
        ) : null}

        {state.kind === 'approved' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              The agency now has access to your account.
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Manage access any time in <strong style={{ color: C.text }}>Account settings</strong> · Agency access.
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setView('account')}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '10px 14px',
                  border: 'none',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Open account settings
              </button>
            </div>
          </>
        ) : null}

        {state.kind === 'declined' ? (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5 }}>
              You declined the request.
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              No access was granted. The agency may send a new request.
            </div>
          </>
        ) : null}

      </Card>
    </div>
  );
}

function formatExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
